import React, { useState, useEffect } from 'react';
import { BookOpen, User, IndianRupee, History, X } from 'lucide-react';
import toast from 'react-hot-toast';

const CreditorLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedParty, setSelectedParty] = useState(null);
  const [partyHistory, setPartyHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  useEffect(() => {
    fetchLedgerSummary();
  }, []);

  const fetchLedgerSummary = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/credit-sales/ledger/summary', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setLedger(data);
      } else {
        setLedger([]);
      }
    } catch (error) {
      console.error("Failed to fetch ledger", error);
      toast.error("Failed to load ledger summary");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPartyHistory = async (partyName) => {
    setIsHistoryLoading(true);
    setSelectedParty(partyName);
    try {
      const res = await fetch(`/api/credit-sales/ledger/history/${encodeURIComponent(partyName)}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setPartyHistory(data);
      } else {
        setPartyHistory([]);
      }
    } catch (error) {
      console.error("Failed to fetch history", error);
      toast.error("Failed to load transaction history");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const closeHistory = () => {
    setSelectedParty(null);
    setPartyHistory([]);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <BookOpen className="text-indigo-500" size={32} />
            Creditor Accounts Ledger
          </h1>
          <p className="text-slate-500 mt-1 font-medium">Live balances of all your creditors</p>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-slate-500">Loading ledger...</div>
        ) : ledger.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No ledger data found. Upload JSON and add payments to see balances.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-5 font-semibold text-slate-600">Party Name</th>
                  <th className="p-5 font-semibold text-slate-600 text-right">Opening Bal. (₹)</th>
                  <th className="p-5 font-semibold text-slate-600 text-right">Total Credit Sales (₹)</th>
                  <th className="p-5 font-semibold text-slate-600 text-right">Total Payments (₹)</th>
                  <th className="p-5 font-semibold text-slate-900 text-right">Outstanding Balance (₹)</th>
                  <th className="p-5 font-semibold text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ledger.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-5 font-medium text-slate-900 flex items-center gap-2">
                      <User className="text-slate-400" size={16} />
                      {item.name}
                    </td>
                    <td className="p-5 text-right font-mono text-indigo-600">{Number(item.opening_balance || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                    <td className="p-5 text-right font-mono text-red-600">{Number(item.total_sales).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                    <td className="p-5 text-right font-mono text-emerald-600">{Number(item.total_payments).toLocaleString('en-IN', {minimumFractionDigits: 2})}</td>
                    <td className="p-5 text-right font-mono font-bold text-slate-800 text-lg">
                      {Number(item.balance).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                    </td>
                    <td className="p-5 text-center">
                      <button 
                        onClick={() => fetchPartyHistory(item.name)}
                        className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg font-medium hover:bg-indigo-100 transition-colors inline-flex items-center gap-2"
                      >
                        <History size={16} />
                        View History
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History Modal */}
      {selectedParty && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">{selectedParty}</h2>
                <p className="text-slate-500 font-medium">Transaction History</p>
              </div>
              <button 
                onClick={closeHistory}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {isHistoryLoading ? (
                <div className="text-center py-10 text-slate-500">Loading history...</div>
              ) : partyHistory.length === 0 ? (
                <div className="text-center py-10 text-slate-500">No transactions found.</div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 text-sm text-slate-500">
                      <th className="pb-3 font-medium">Date</th>
                      <th className="pb-3 font-medium">Particulars</th>
                      <th className="pb-3 font-medium text-right">Debit (Sale)</th>
                      <th className="pb-3 font-medium text-right">Credit (Payment)</th>
                      <th className="pb-3 font-medium text-right">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {partyHistory.map((txn, idx) => {
                      const debit = parseFloat(txn.debit) || 0;
                      const credit = parseFloat(txn.credit) || 0;
                      const isPayment = credit > 0;
                      
                      const dateObj = new Date(txn.business_date);
                      const formattedDate = !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString('en-GB') : txn.business_date;
                      
                      return (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 text-slate-700">{formattedDate}</td>
                          <td className="py-4">
                            <span className={`font-medium ${isPayment ? 'text-emerald-600' : 'text-slate-800'}`}>
                              {txn.item_name || 'Credit Sale'}
                            </span>
                            {!isPayment && txn.quantity && (
                              <span className="text-slate-500 text-sm ml-2">({Number(txn.quantity).toFixed(2)} Ltr)</span>
                            )}
                          </td>
                          <td className="py-4 text-right font-mono text-slate-800">
                            {debit > 0 ? debit.toLocaleString('en-IN', {minimumFractionDigits: 2}) : '-'}
                          </td>
                          <td className="py-4 text-right font-mono text-emerald-600">
                            {credit > 0 ? credit.toLocaleString('en-IN', {minimumFractionDigits: 2}) : '-'}
                          </td>
                          <td className="py-4 text-right font-mono font-bold text-slate-900">
                            {Number(txn.balance).toLocaleString('en-IN', {minimumFractionDigits: 2})}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditorLedger;
